<template>
  <div class="mt-2 flex gap-8">
    <div class="grow">
      <div class="mb-6 flex flex-wrap gap-6">
        <ClientStatistic />
        <div class="bg-base-200 flex flex-wrap items-end gap-2 p-4">
          <div>
            <legend class="fieldset-legend">Tên máy</legend>
            <DebounceInput placeholder="Ví dụ: DESKTOP-ABC" v-model="keyword" />
          </div>
          <div>
            <legend class="fieldset-legend">Tạo sau ngày</legend>
            <label class="input w-full">
              <input type="date" v-model="startDate" />
            </label>
          </div>
          <div>
            <legend class="fieldset-legend">Tạo trước ngày</legend>
            <label class="input w-full">
              <input type="date" v-model="endDate" />
            </label>
          </div>
          <div>
            <legend class="fieldset-legend"></legend>
            <label>
              <button @click="resetFilters" class="btn btn-outline btn-primary">
                <i class="fa-solid fa-rotate-left me-2 opacity-90"></i>
                Đặt lại
              </button>
            </label>
          </div>
        </div>
      </div>
      <template v-if="isLoading">
        <Loading />
      </template>
      <template v-else-if="data && data.data && data.data.length > 0">
        <Pagination
          class="mb-4"
          v-if="data.data.length >= 24"
          :total_pages="totalPages"
        />
        <div
          class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
        >
          <RouterLink
            v-for="cp in data.data"
            :key="cp.uuid"
            :to="{
              name: 'ClientInfo',
              params: {
                client_id: cp.uuid,
              },
            }"
          >
            <ClientCard
              class="cursor-pointer hover:brightness-95"
              :is-online="cp.status.toLowerCase() === 'online'"
              :computer-name="cp.computer_name"
              :cpu="cp.cpu"
              :ip="cp.client_ip"
              :os="cp.os"
              :country="cp.country"
              :last-connection="cp.last_connection"
              :uninstalled="cp.uninstalled"
              :version="cp.version"
              @contextmenu.prevent="
                (event: MouseEvent) => showContextMenu(event, cp)
              "
            />
          </RouterLink>
        </div>
        <Pagination :total_pages="totalPages" />
      </template>
      <template v-else>
        <NotFound />
      </template>
    </div>
  </div>
  <ul
    v-if="showMenu"
    ref="menuRef"
    class="menu bg-base-100 rounded-box fixed z-1 p-2 shadow"
    :style="{ left: menuPosition.x + 'px', top: menuPosition.y + 'px' }"
  >
    <li>
      <a @click="handleMenuAction('view')">
        <i class="fas fa-eye mr-2 opacity-90"></i>Xem chi tiết
      </a>
    </li>
    <li v-if="!selectedClient?.uninstalled">
      <a @click="handleMenuAction('delete')">
        <i class="fas fa-trash mr-2 opacity-90"></i>Xóa máy trạm
      </a>
    </li>
  </ul>
  <ConfirmDeleteClient
    :agent="selectedClient"
    v-model:open="openConfirm"
    :show-button="false"
    @deleted="queryClient.invalidateQueries({ queryKey: ['clients'] })"
  />
</template>

<script setup lang="ts">
import ConfirmDeleteClient from "@/components/ClientDetail/Setting/ConfirmDeleteClient.vue";
import ClientCard from "@/components/ClientList/ClientCard.vue";
import ClientStatistic from "@/components/ClientList/ClientStatistic.vue";
import DebounceInput from "@/components/DebounceInput.vue";
import Loading from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import Pagination from "@/components/PaginationTemplate.vue";
import { useClientsQuery } from "@/composables/clients";
import { router } from "@/router/router";
import type { Client } from "@/types/clients";
import { useQueryClient } from "@tanstack/vue-query";
import { onClickOutside } from "@vueuse/core";
import { computed, ref, watch } from "vue";

const campaignId = computed(() => {
  return router.currentRoute.value.params.campaign_id?.toString() ?? undefined;
});
const { data, isLoading, totalPages } = useClientsQuery(campaignId.value);

const queryClient = useQueryClient();

const menuRef = ref<HTMLElement>();
const showMenu = ref(false);
const menuPosition = ref({ x: 0, y: 0 });
const selectedClient = ref<Client>();
const openConfirm = ref(false);

const keyword = ref<string>();
const startDate = ref<string>();
const endDate = ref<string>();
watch(
  [keyword, startDate, endDate],
  () => {
    const query: Record<string, string> = {};
    if (keyword.value !== undefined) {
      query.keyword = keyword.value;
    }
    if (startDate.value !== undefined) {
      query.start_date = startDate.value;
    }
    if (endDate.value !== undefined) {
      query.end_date = endDate.value;
    }
    router.replace({
      name: router.currentRoute.value.name as string,
      query: {
        ...router.currentRoute.value.query,
        ...query,
      },
    });
  },
  { immediate: true },
);

const resetFilters = () => {
  keyword.value = "";
  startDate.value = "";
  endDate.value = "";
};

const showContextMenu = (event: MouseEvent, client: Client) => {
  event.preventDefault();
  selectedClient.value = client;
  menuPosition.value = { x: event.clientX, y: event.clientY };
  showMenu.value = true;
};

onClickOutside(menuRef, () => {
  showMenu.value = false;
});

const handleMenuAction = (action: string) => {
  showMenu.value = false;
  // Handle menu actions here
  switch (action) {
    case "view":
      router.push({
        name: "ClientInfo",
        params: { client_id: selectedClient.value?.uuid },
      });
      break;
    case "delete":
      openConfirm.value = true;
      break;
  }
};
</script>
